import React from "react";
import { FrontColors } from "../../../models/FrontColors";
import { FrontSpacing } from "../../../models/FrontSpacing";
import { Background } from "../../BuildingBlocks/Background";
import { Flex } from "../../BuildingBlocks/Flex";
import { FontQuicksand } from "../../BuildingBlocks/FontQuicksand";
import { FontSize } from "../../BuildingBlocks/FontSize";
import { MaxWidth } from "../../BuildingBlocks/MaxWidth";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { TextAlign } from "../../BuildingBlocks/TextAlign";
import { TextColor } from "../../BuildingBlocks/TextColor";
import { Width } from "../../BuildingBlocks/Width";
import { WeightBold } from "../../WeightBold/WeightBold";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";
import { FPButton } from "../Complex/FPButton/FPButton";
import { FPInput } from "../Complex/FPInput/FPInput";
import { FPLandingTitle } from "../Simple/FPLandingTitle/FPLandingTitle";
import { FPSimpleLogo } from "../Simple/FPSimpleLogo/FPSimpleLogo";
import { TPublicPropertyReportIntroducerOption } from "../../../../../domain/codecs/form/PublicPropertyReportForm";

/**
 * The introducer picker shown to a Sail staff member before they key in a
 * valuation.
 *
 * **When to use:** the property report page's staff branch. Partners like
 * Affinion send valuations by bulk email and have no email domain, so nobody
 * there can sign in and log them — a staff member does it instead, and has to
 * say which firm the report belongs to. Nothing is preselected on purpose: the
 * report buys a Land Registry search against whichever firm is picked.
 *
 * @example
 * <FPPropertyReportIntroducerSelect
 *     options={[{id: "abc", name: "Affinion"}]}
 *     filter={filter}
 *     onChangeFilter={setFilter}
 *     onSelect={select}
 * />
 */
export const FPPropertyReportIntroducerSelect = (props: {
    options: Array<TPublicPropertyReportIntroducerOption>;
    filter: string;
    onChangeFilter: (filter: string) => void;
    onSelect: (introducerId: string) => void;
}): JSX.Element => {
    const normalisedFilter = props.filter.trim().toLowerCase();

    const visibleOptions =
        normalisedFilter === ""
            ? props.options
            : props.options.filter(({name}) => name.toLowerCase().includes(normalisedFilter));

    const onEnter = () => {
        if (visibleOptions.length === 1) {
            props.onSelect(visibleOptions[0].id);
        }
    };

    return <SpacingColumn
        spacing={FrontSpacing.LARGE_1}
        justifyContent="center"
    >
        {/* LOGO */}
        <Flex justifyContent="center">
            <FPSimpleLogo width="95px" />
        </Flex>

        {/* HEADER */}
        <Flex justifyContent="center">
            <MaxWidth width="530px">
                <FPLandingTitle
                    size={60}
                    color={FrontColors.NEUTRAL_18}
                    align="center"
                >
                    Who is this valuation for?
                </FPLandingTitle>
            </MaxWidth>
        </Flex>

        {/* SUBTITLE */}
        <Flex justifyContent="center">
            <MaxWidth width="425px">
                <TextColor color={FrontColors.NEUTRAL_18}>
                    <TextAlign position="center">
                        <FontQuicksand>
                            <FontSize
                                size={20}
                                lineHeight={1.2}
                            >
                                <WeightSemiBold>
                                    <WeightBold>Pick the firm this valuation came from.</WeightBold> We log the report against them, and they pay for the Land Registry search. <WeightBold>Choose the wrong one and the wrong firm gets the bill.</WeightBold>
                                </WeightSemiBold>
                            </FontSize>
                        </FontQuicksand>
                    </TextAlign>
                </TextColor>
            </MaxWidth>
        </Flex>

        {/* FILTER */}
        <Flex justifyContent="center">
            <Width width="425px">
                <FPInput
                    placeholder="Search for a firm"
                    value={props.filter}
                    coloursInverted={true}
                    autoFocus={true}
                    onChange={props.onChangeFilter}
                    onEnter={onEnter}
                />
            </Width>
        </Flex>

        {/* WHITE AREA */}
        <Flex justifyContent="center">
            <Background
                color={FrontColors.NEUTRAL_18}
                borderRadius="10px"
                width="700px"
                padding={FrontSpacing.LARGE_1}
            >
                <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                    {visibleOptions.map((option) =>
                        <Width
                            key={option.id}
                            width="fit-content"
                        >
                            <FPButton
                                label={<WeightBold>{option.name}</WeightBold>}
                                onClick={() => props.onSelect(option.id)}
                            />
                        </Width>
                    )}

                    {/* NO MATCHES */}
                    {visibleOptions.length === 0 &&
                        <FontQuicksand>
                            <FontSize size="20px">
                                <TextColor color={FrontColors.NEUTRAL_2}>
                                    <WeightSemiBold>
                                        No firms match that search.
                                    </WeightSemiBold>
                                </TextColor>
                            </FontSize>
                        </FontQuicksand>
                    }
                </SpacingColumn>
            </Background>
        </Flex>
    </SpacingColumn>;
};
